import React, { useState, useCallback, useEffect } from "react";
import { Card, Button, Table, Spinner, Form, Row, Col } from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faSearch, faHistory } from "@fortawesome/free-solid-svg-icons";
import { client } from "../../api/client";

// ─── Helpers ────────────────────────────────────────────────────────────────

const todayISO = () => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Port-au-Prince",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const v = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  return `${v.year}-${v.month}-${v.day}`;
};

const fieldStyle = {
  borderRadius: "4px",
  border: "1px solid #28A745",
  paddingRight: "2.2rem",
};

const FieldIcon = () => (
  <FontAwesomeIcon
    icon={faCheck}
    className="text-success"
    style={{
      position: "absolute",
      right: "12px",
      top: "50%",
      transform: "translateY(-50%)",
      pointerEvents: "none",
    }}
  />
);

// ─── Component ───────────────────────────────────────────────────────────────

export default function Tracabilite() {
  const [dateDebut, setDateDebut] = useState(todayISO());
  const [dateFin, setDateFin] = useState(todayISO());
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searched, setSearched] = useState(false);

  // ── Fetch logs ─────────────────────────────────────────────────────────────
  const fetchLogs = useCallback(async () => {
    if (!dateDebut || !dateFin) {
      setError("Veuillez sélectionner une date de début et de fin.");
      return;
    }
    setLoading(true);
    setError("");
    setSearched(true);
    try {
      const data = await client(
        `audit-logs/?date_debut=${dateDebut}&date_fin=${dateFin}`
      );
      setLogs(data?.results || []);
    } catch (e) {
      console.error("Tracabilite fetchLogs error:", e);
      setError("Erreur lors du chargement du journal d'audit. Veuillez réessayer.");
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [dateDebut, dateFin]);

  // Charger automatiquement les logs du jour à l'ouverture
  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="d-flex flex-column gap-4 p-2">
      {/* ── Filtre de date ───────────────────────────────────────────────── */}
      <Card border="0" className="shadow-sm">
        <Card.Body>
          <h5 className="mb-3 d-flex align-items-center gap-2" style={{ color: "#1D1D1F", fontWeight: 600 }}>
            <FontAwesomeIcon icon={faHistory} style={{ color: "#007BFF" }} />
            Traçabilité — Journal d'Audit
          </h5>

          <Row className="align-items-end g-3">
            {/* Debut */}
            <Col xs={12} sm={6} md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold small mb-1">Debut</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type="date"
                    value={dateDebut}
                    onChange={(e) => setDateDebut(e.target.value)}
                    style={fieldStyle}
                  />
                  <FieldIcon />
                </div>
              </Form.Group>
            </Col>

            {/* Fin */}
            <Col xs={12} sm={6} md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold small mb-1">Fin</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type="date"
                    value={dateFin}
                    onChange={(e) => setDateFin(e.target.value)}
                    style={fieldStyle}
                  />
                  <FieldIcon />
                </div>
              </Form.Group>
            </Col>

            {/* Bouton Rechercher */}
            <Col xs={12} sm={12} md={4}>
              <Button
                variant="primary"
                className="w-100"
                style={{
                  backgroundColor: "#007BFF",
                  borderColor: "#007BFF",
                  fontWeight: 600,
                  borderRadius: "6px",
                }}
                onClick={fetchLogs}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Chargement...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSearch} className="me-2" />
                    Rechercher
                  </>
                )}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ── Résultats ────────────────────────────────────────────────────── */}
      <Card border="0" className="shadow-sm">
        <Card.Body className="p-0">
          {/* En-tête résultats */}
          <div
            className="d-flex align-items-center justify-content-between px-4 py-3"
            style={{ borderBottom: "1px solid #E5E5E7" }}
          >
            <span className="fw-semibold" style={{ color: "#1D1D1F" }}>
              Résultats
            </span>
            {searched && !loading && (
              <span
                className="badge rounded-pill"
                style={{
                  backgroundColor: "#007BFF",
                  color: "#fff",
                  fontWeight: 500,
                  fontSize: "0.78rem",
                  padding: "4px 10px",
                }}
              >
                {logs.length} entrée{logs.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {/* Erreur */}
          {error && (
            <div className="px-4 py-3">
              <div
                className="alert alert-danger mb-0 py-2 px-3"
                style={{ borderRadius: "6px", fontSize: "0.875rem" }}
              >
                {error}
              </div>
            </div>
          )}

          {/* Spinner */}
          {loading && (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted small">Chargement du journal…</p>
            </div>
          )}

          {/* Table */}
          {!loading && !error && (
            <div className="table-responsive p-3">
              <Table bordered hover className="align-middle mb-0" style={{ borderColor: "#DDD" }}>
                <thead>
                  <tr style={{ backgroundColor: "#F8F9FA" }}>
                    <th style={{ fontSize: "0.9rem", fontWeight: "bold" }}>Utilisateur</th>
                    <th style={{ fontSize: "0.9rem", fontWeight: "bold" }}>Action</th>
                    <th style={{ fontSize: "0.9rem", fontWeight: "bold", width: "15%" }}>Date</th>
                    <th style={{ fontSize: "0.9rem", fontWeight: "bold", width: "15%" }}>Heure</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center text-muted py-5">
                        {searched
                          ? "Aucune entrée trouvée pour cette période."
                          : "Utilisez le filtre ci-dessus pour rechercher."}
                      </td>
                    </tr>
                  ) : (
                    logs.map((log) => (
                      <tr key={log.id}>
                        <td className="fw-bold" style={{ color: "#222", fontSize: "0.9rem" }}>
                          {log.utilisateur}
                        </td>
                        <td style={{ color: "#444", fontSize: "0.9rem" }}>
                          {log.action}
                        </td>
                        <td style={{ color: "#444", fontSize: "0.9rem" }}>
                          {log.date}
                        </td>
                        <td style={{ color: "#444", fontSize: "0.9rem" }}>
                          {log.heure}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}


